import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Card,IconButton,Stack,Switch,Typography } from "@mui/material";
import { useMutation,useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { useContext,useEffect,useState } from "react";
import { toast } from "react-toastify";
import { NewFormContext } from "../../context/NewFormContext";
import AttToken from "../../helpers/attToken";
import QrCode from '../../service/QrCode';
import type { CreateUpdateBody,ModeloQRCode,qrCodeResponse } from '../../types/checklist/types';
import RenderQrCode from "../Reports/RenderQrCode";

const qrCodeService = new QrCode();

export default function QrCodeList() {
  const { modelo } = useContext(NewFormContext);
  const [listQrCodes, setListQrCodes] = useState<qrCodeResponse[]>([]);
  const [show, setShow] = useState(false);

  const showQrCode = () => {
    setShow(!show);
  };

  const { data: qrCodeData, isLoading, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['GET_QRCODES'],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        if (token) {
          const response: qrCodeResponse[] = await qrCodeService.getList({ ...token, formId: modelo?.id as string });
          // if (!response) throw new Error(t('custom.erro_ao_buscar_lista_de_qr_cod'));
          return response;
        }
      } catch (error) {
        console.error('Error fetching QR codes:', error);
        throw error;
      }
    },
    refetchOnMount: 'always',
    retry: 0,
    refetchOnWindowFocus: false
  });

  const { mutate, isPending } = useMutation({
    mutationKey: ['CHANGE_QRCODE_STATUS'],
    mutationFn: async (qrCode: CreateUpdateBody) => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        if (token) {
          await qrCodeService.createUpdateQrCode({ ...token, ...qrCode });
        }
      } catch (error) {
        console.error('Error updating QR code status:', error);
        throw error;
      }
    }
  });

  useEffect(() => {
    if (isSuccess && qrCodeData) {
      setListQrCodes(qrCodeData);
    }
  }, [isLoading, isFetching, isSuccess, qrCodeData]);

  if (isError) {
    return toast.error(t('custom.erro_ao_buscar_lista_de_qr_cod'), { toastId: 'custom.erro_ao_buscar_lista_de_qr_cod' });
  }

  return (
    <Card sx={{ width: '100%', padding: 2 }}>
      <Stack
        width='100%'
        direction="column"
        justifyContent="center"
        alignItems='center'
        spacing={2}
      >
        <Typography
          color='textDisabled'
          variant='h5'
          textAlign='center'
          fontWeight={600}
          width='100%'>
          {t('checklists.qrCodeList.title')}
        </Typography>
        {isLoading || isFetching
          ? <Typography variant="h5" color="success" fontWeight={600} textAlign='center'>{t('checklists.qrCodeList.loading')}</Typography>
          : <>
            {!qrCodeData?.length ?
              <>
                <div className="tag is-warning">
                  <Typography variant="h6" textAlign='center'>
                    {t('checklists.qrCodeList.notConfigured')}
                  </Typography>
                </div>
                <Typography variant="h6" fontWeight={600} color='textDisabled'>
                  {t('checklists.qrCodeList.instructions')}
                </Typography>
              </>
              : <>
                <Typography variant="h6" fontWeight={600} color='textDisabled'>{t('checklists.qrCodeList.listByTeams')}</Typography>
                {listQrCodes?.map((qrCode) => (
                  <Card sx={{ width: '100%', padding: 3 }}>
                    <Stack direction='column' justifyContent='flex-start' alignItems='center' spacing={2.5}>
                      <Stack direction='row' justifyContent='space-between' width='100%'>
                        <Typography color='textDisabled' fontWeight={600}>
                          {qrCode.data?.id}
                        </Typography>
                        <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={2}>
                          <Switch
                            disabled={isPending}
                            onChange={(e) => {
                              const value = e.target.checked;
                              const updatedQrCode = {
                                ...qrCode.data,
                                status: value,
                                respostasModelo: qrCode.data.respostasModelo
                              };
                              setListQrCodes((prev) =>
                                prev?.map((qr) =>
                                  qr.data.id === qrCode.data.id
                                    ? {
                                      ...qr,
                                      data: {
                                        ...qr.data,
                                        status: value
                                      }
                                    }
                                    : qr)
                              );
                              mutate(updatedQrCode as CreateUpdateBody);
                            }}
                            checked={qrCode.data.status} />
                          {!show ? <IconButton onClick={showQrCode}>
                            <VisibilityIcon />
                          </IconButton> :
                            <IconButton onClick={showQrCode}>
                              <VisibilityOffIcon />
                            </IconButton>}
                        </Stack>
                      </Stack>
                      {show && <RenderQrCode
                        qrCodeData={qrCode.deepLink}
                        renderBody={qrCode?.data}
                        modeloQrCode={modelo?.modeloQrCode as ModeloQRCode}
                        hideSpan
                      />}
                    </Stack>
                  </Card>
                ))}
              </>}
          </>
        }
      </Stack>
    </Card>
  );
}
